import { ReactElement, FormEvent, useContext } from "react";
import { Form, FormField, TextInput } from "@justworkshr/milo-form";
import { Button } from "@justworkshr/milo-core";

import { MFA_FACTOR_COPY } from "../../constants/factorCopy";

import { Factor, FactorTypeEnum } from "types/factor/factor";
import { OktaError } from "types/oktaError/oktaError";

import useEnrollPhoneFactor, {
  EnrollPhoneData,
} from "hooks/useEnrollPhoneFactor";
import usePhoneEnrollStateManager from "../../hooks/usePhoneEnrollStateManager";
import useAuthEnrollPhone from "pages/register/hooks/useAuthEnrollPhone";
import useCancelMfaEnrollment from "pages/register/hooks/useCancelMfaEnrollment";

import AuthTransactionContext from "pages/register/contexts/AuthTransactionContext";

import styles from "./phone-number-input.module.css";
import {
  AuthTransaction,
  StatusEnum,
} from "pages/register/types/authTransaction";

const PhoneNumberInput = ({ factor }: { factor: Factor }): ReactElement => {
  const { transaction, setTransaction } = useContext(AuthTransactionContext);

  const { mutate: enrollPhoneFactor, isLoading: isLoadingEnrollPhone } =
    useEnrollPhoneFactor();
  const { mutate: authEnrollPhoneFactor, isLoading: isLoadingAuthEnrollPhone } =
    useAuthEnrollPhone();
  const {
    mutate: cancelMfaEnrollment,
    isLoading: isLoadingCancelMfaEnrollment,
  } = useCancelMfaEnrollment();
  const isLoadingPhoneEnrollment = transaction
    ? isLoadingAuthEnrollPhone
    : isLoadingEnrollPhone;

  const {
    phoneInput,
    setPhoneInput,
    errorMessage,
    setErrorMessage,
    setSubmittedPhoneNumber,
    isEnrollmentDisabled,
  } = usePhoneEnrollStateManager();

  const isSms = factor.factorType === FactorTypeEnum.Sms;

  const handleSubmitPhoneNumber = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");

    const enrollPhoneData: EnrollPhoneData = {
      factorType: isSms ? FactorTypeEnum.Sms : FactorTypeEnum.Call,
      phoneNumber: phoneInput,
    };
    const handleEnrollPhoneError = (errorResponse: OktaError) => {
      const DEFAULT_ERROR_MESSAGE = "This phone number is invalid.";

      // TODO: Update with problemDetails solution
      setErrorMessage(errorResponse?.errorSummary ?? DEFAULT_ERROR_MESSAGE);
    };

    const authEnrollPhoneFactorCallback = (transaction: AuthTransaction) => {
      authEnrollPhoneFactor(
        {
          stateToken: transaction.data.stateToken,
          ...enrollPhoneData,
        },
        {
          onSuccess: (transaction) => {
            setSubmittedPhoneNumber(phoneInput);
            setTransaction(transaction);
          },
          onError: handleEnrollPhoneError,
        },
      );
    };

    if (transaction) {
      if (transaction.status === StatusEnum.MfaEnrollActivate) {
        cancelMfaEnrollment(
          { stateToken: transaction.data.stateToken },
          {
            onSuccess: (transaction) => {
              setTransaction(transaction);
              authEnrollPhoneFactorCallback(transaction);
            },
            onError: handleEnrollPhoneError,
          },
        );
      } else {
        authEnrollPhoneFactorCallback(transaction);
      }
    } else {
      enrollPhoneFactor(enrollPhoneData, {
        onSuccess: () => setSubmittedPhoneNumber(phoneInput),
        onError: handleEnrollPhoneError,
      });
    }
  };

  return (
    <Form onSubmit={handleSubmitPhoneNumber}>
      <FormField
        name="phone_number"
        label="Enter your phone number"
        required={true}
        error={errorMessage}
      >
        <>
          <div className={styles.inputDescription}>
            {MFA_FACTOR_COPY[factor.key].setUpDetails}
          </div>

          <div className={`${styles.inputFieldRow}`}>
            <div className={styles.textInput}>
              <TextInput
                name="phoneNumber"
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
              />
            </div>

            <Button
              type="submit"
              variant="outlined"
              className={styles.submitButton}
              disabled={isEnrollmentDisabled}
              loading={isLoadingPhoneEnrollment || isLoadingCancelMfaEnrollment}
            >
              {isSms ? "Send Code" : "Call Now"}
            </Button>
          </div>
        </>
      </FormField>
    </Form>
  );
};

export default PhoneNumberInput;
